import { useMutation } from '@apollo/client';
import { Popover, Stack } from '@sixfold/common-ui';
import { gql } from 'graphql-tag';
import React, { useState } from 'react';

import { Button } from '../../components/button';
import {
  SensitiveConnectionDataQueryVariables,
  ValidateTelematicsIntegrationMutation,
  ValidateTelematicsIntegrationMutationVariables,
} from '../../lib/graphql';

type IntegrationValidationInput = {
  connection_data?: ValidateTelematicsIntegrationMutationVariables['input']['connection_data'];
  telematics_provider_id: ValidateTelematicsIntegrationMutationVariables['input']['telematics_provider_id'];
  telematics_integration_id?: SensitiveConnectionDataQueryVariables['telematics_integration_id'];
};

interface Props {
  integration: IntegrationValidationInput;
}

const validateTelematicsIntegrationMutation = gql`
  mutation ValidateTelematicsIntegration($input: ValidateTelematicsIntegrationInput!) {
    validateTelematicsIntegration(input: $input) {
      areValidCredentials
    }
  }
`;

export const ValidateIntegrationButton: React.FunctionComponent<Props> = ({ integration }) => {
  const [validateTelematicsIntegration] = useMutation<
    ValidateTelematicsIntegrationMutation,
    ValidateTelematicsIntegrationMutationVariables
  >(validateTelematicsIntegrationMutation);

  const [areValidCredentials, setAreValidCredentials] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleValidationClick = async () => {
    try {
      const result = await validateTelematicsIntegration({
        variables: {
          input: {
            telematics_provider_id: integration.telematics_provider_id,
            telematics_integration_id: integration.telematics_integration_id,
            connection_data: integration.connection_data,
          },
        },
      });
      const valid = result.data?.validateTelematicsIntegration?.areValidCredentials;

      if (valid === undefined) {
        return;
      }

      setAreValidCredentials(valid);
      setError(null);
    } catch (e) {
      setAreValidCredentials(false);
      setError((e as Error).message);
    }
  };

  const invalidButton = (
    <Stack direction="row">
      <Button kind="danger" size="small" style={{ cursor: 'default' }}>
        Invalid
      </Button>
      <Button size="small" onClick={() => handleValidationClick()}>
        Check
      </Button>
    </Stack>
  );

  const validButton = (
    <Stack direction="row">
      <Button kind="primary" size="small" style={{ cursor: 'default' }}>
        Valid
      </Button>
      <Button size="small" onClick={() => handleValidationClick()}>
        Check
      </Button>
    </Stack>
  );

  if (error !== null) {
    return (
      <Popover
        isVisible
        placement="top-end"
        content={
          <>
            <b>Error occured</b> {error}
          </>
        }>
        {invalidButton}
      </Popover>
    );
  }

  if (areValidCredentials === null) {
    return (
      <Button size="small" onClick={() => handleValidationClick()}>
        Check
      </Button>
    );
  }

  return areValidCredentials ? validButton : invalidButton;
};
